import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Users, DollarSign, Building2, Briefcase, MapPin, Info, Calculator, BarChart as ChartBar, PiggyBank, CreditCard, Wallet } from 'lucide-react';
import { FinancialChart } from './Chart';
import { Tooltip } from './Tooltip';
import { STATE_TAX_RATES } from '../constants';

interface BudgetAnalysisProps {
  income: number;
  expenses: {
    needs: number;
    wants: number;
    savings: number;
  };
  state: string;
  filingStatus: 'single' | 'joint' | 'head';
}

export const BudgetAnalysis: React.FC<BudgetAnalysisProps> = ({
  income,
  expenses,
  state,
  filingStatus
}) => {
  // 2024 US Census Bureau Data
  const nationalMedianIncome = {
    single: 44740,
    joint: 91010,
    head: 57780
  };

  // Cost of Living Index by State (100 = national average)
  const costOfLivingIndex = {
    'Alabama': 88.1,
    'Alaska': 127.1,
    'Arizona': 102.2,
    'Arkansas': 89.9,
    'California': 151.7,
    'Colorado': 105.3,
    'Connecticut': 121.6,
    'Delaware': 102.6,
    'Florida': 97.9,
    'Georgia': 89.2,
    'Hawaii': 193.3,
    'Idaho': 97.7,
    'Illinois': 94.5,
    'Indiana': 90.0,
    'Iowa': 89.9,
    'Kansas': 86.5,
    'Kentucky': 93.6,
    'Louisiana': 89.3,
    'Maine': 117.5,
    'Maryland': 124.0,
    'Massachusetts': 135.0,
    'Michigan': 89.3,
    'Minnesota': 97.2,
    'Mississippi': 83.3,
    'Missouri': 88.9,
    'Montana': 106.9,
    'Nebraska': 90.8,
    'Nevada': 108.5,
    'New Hampshire': 115.0,
    'New Jersey': 115.2,
    'New Mexico': 88.4,
    'New York': 148.2,
    'North Carolina': 94.9,
    'North Dakota': 98.1,
    'Ohio': 90.8,
    'Oklahoma': 86.8,
    'Oregon': 130.1,
    'Pennsylvania': 101.7,
    'Rhode Island': 119.4,
    'South Carolina': 95.9,
    'South Dakota': 99.8,
    'Tennessee': 89.7,
    'Texas': 91.5,
    'Utah': 98.4,
    'Vermont': 114.5,
    'Virginia': 101.8,
    'Washington': 110.7,
    'West Virginia': 91.1,
    'Wisconsin': 97.3,
    'Wyoming': 89.3
  };

  // Industry-standard financial ratios and benchmarks
  const financialRatios = {
    debtToIncome: {
      excellent: 0.15,
      good: 0.30,
      fair: 0.40,
      poor: 0.43
    },
    savingsRate: {
      excellent: 0.20,
      good: 0.15,
      fair: 0.10,
      poor: 0.05
    },
    housingExpenseRatio: {
      excellent: 0.25,
      good: 0.30,
      fair: 0.35,
      poor: 0.40
    },
    emergencyFund: {
      excellent: 6,
      good: 4,
      fair: 3,
      poor: 1
    }
  };

  const annualIncome = income * 12;
  const medianIncome = nationalMedianIncome[filingStatus];
  const stateMedian = medianIncome * (costOfLivingIndex[state] / 100);
  
  // Calculate income percentile based on position relative to median
  const calculatePercentile = () => {
    if (annualIncome === medianIncome) return 50;
    if (annualIncome > medianIncome) {
      return Math.min(50 + ((annualIncome - medianIncome) / medianIncome) * 50, 99);
    }
    return Math.max((annualIncome / medianIncome) * 50, 1);
  };

  const incomePercentile = calculatePercentile();

  // Calculate expense ratios
  const totalExpenses = expenses.needs + expenses.wants + expenses.savings;
  const expenseRatio = {
    needs: (expenses.needs / income) * 100,
    wants: (expenses.wants / income) * 100,
    savings: (expenses.savings / income) * 100
  };

  // Calculate financial health scores
  const calculateFinancialHealthScore = () => {
    let score = 0;
    
    // Savings rate score (max 30 points)
    const savingsRate = expenses.savings / income;
    if (savingsRate >= financialRatios.savingsRate.excellent) score += 30;
    else if (savingsRate >= financialRatios.savingsRate.good) score += 25;
    else if (savingsRate >= financialRatios.savingsRate.fair) score += 15;
    else if (savingsRate >= financialRatios.savingsRate.poor) score += 5;

    // Expense management score (max 40 points)
    const needsRatio = expenses.needs / income;
    if (needsRatio <= 0.5) score += 20;
    else if (needsRatio <= 0.6) score += 15;
    else if (needsRatio <= 0.7) score += 10;
    else score += 5;

    const wantsRatio = expenses.wants / income;
    if (wantsRatio <= 0.3) score += 20;
    else if (wantsRatio <= 0.35) score += 15;
    else if (wantsRatio <= 0.4) score += 10;
    else score += 5;

    // Income utilization score (max 30 points)
    const utilizationRatio = totalExpenses / income;
    if (utilizationRatio <= 0.8) score += 30;
    else if (utilizationRatio <= 0.9) score += 20;
    else if (utilizationRatio <= 1) score += 10;

    return score;
  };

  const financialHealthScore = calculateFinancialHealthScore();

  // Calculate recommended adjustments
  const calculateRecommendedAdjustments = () => {
    const adjustments = [];
    const monthlyIncome = income;

    // Check savings rate
    const currentSavingsRate = (expenses.savings / monthlyIncome);
    if (currentSavingsRate < financialRatios.savingsRate.good) {
      const recommendedSavings = monthlyIncome * financialRatios.savingsRate.good;
      adjustments.push({
        category: 'Savings',
        current: expenses.savings,
        recommended: recommendedSavings,
        difference: recommendedSavings - expenses.savings
      });
    }

    // Check needs spending
    if (expenseRatio.needs > 50) {
      const recommendedNeeds = monthlyIncome * 0.5;
      adjustments.push({
        category: 'Needs',
        current: expenses.needs,
        recommended: recommendedNeeds,
        difference: recommendedNeeds - expenses.needs
      });
    }

    // Check wants spending
    if (expenseRatio.wants > 30) {
      const recommendedWants = monthlyIncome * 0.3;
      adjustments.push({
        category: 'Wants',
        current: expenses.wants,
        recommended: recommendedWants,
        difference: recommendedWants - expenses.wants
      });
    }

    return adjustments;
  };

  const recommendedAdjustments = calculateRecommendedAdjustments();

  // Prepare chart data
  const expenseDistributionData = {
    labels: ['Housing', 'Transportation', 'Food', 'Insurance', 'Healthcare', 'Entertainment', 'Clothing', 'Education', 'Other'],
    datasets: [
      {
        label: 'Your Expenses',
        data: [
          expenses.needs * 0.35,  // Housing
          expenses.needs * 0.15,  // Transportation
          expenses.needs * 0.25,  // Food
          expenses.needs * 0.10,  // Insurance
          expenses.needs * 0.10,  // Healthcare
          expenses.wants * 0.40,  // Entertainment
          expenses.wants * 0.20,  // Clothing
          expenses.wants * 0.20,  // Education
          expenses.wants * 0.20   // Other
        ],
        backgroundColor: 'rgba(16, 185, 129, 0.5)',
        borderColor: '#10b981',
        borderWidth: 1
      },
      {
        label: 'National Average',
        data: [34.9, 16.0, 12.0, 11.8, 8.1, 5.2, 2.6, 2.2, 7.2],
        backgroundColor: 'rgba(52, 211, 153, 0.5)',
        borderColor: '#34d399',
        borderWidth: 1
      }
    ]
  };

  const incomeComparisonData = {
    labels: ['Your Income', 'State Median', 'National Median'],
    datasets: [{
      data: [annualIncome, stateMedian, medianIncome],
      backgroundColor: ['#10b981', '#34d399', '#6ee7b7'],
      borderWidth: 0
    }]
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-xl bg-emerald-500/10">
          <ChartBar className="w-6 h-6 text-emerald-500" />
        </div>
        <div className="flex items-center gap-2">
          <h2 className="text-2xl font-semibold">Financial Analysis</h2>
          <Tooltip
            title="Understanding Your Financial Analysis"
            content="This analysis provides a comprehensive view of your financial position, comparing your income and expenses to national averages and recommended benchmarks. The analysis includes income comparisons, expense ratios, and personalized recommendations for optimizing your budget."
            position="right"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="glass-card p-6 border border-emerald-500/20"
        >
          <div className="flex items-center gap-3 mb-4">
            <Calculator className="w-5 h-5 text-emerald-500" />
            <h3 className="font-semibold">Financial Health Score</h3>
          </div>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-baseline mb-2">
                <span className="text-sm text-slate-400">Overall Score</span>
                <span className={`text-lg font-bold ${
                  financialHealthScore >= 80 ? 'text-emerald-500' :
                  financialHealthScore >= 60 ? 'text-amber-500' :
                  'text-red-500'
                }`}>{financialHealthScore}/100</span>
              </div>
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className={`h-full ${
                    financialHealthScore >= 80 ? 'bg-emerald-500' :
                    financialHealthScore >= 60 ? 'bg-amber-500' :
                    'bg-red-500'
                  }`}
                  style={{ width: `${financialHealthScore}%` }}
                />
              </div>
            </div>
            <p className="text-sm text-slate-400">
              {financialHealthScore >= 80 ? 'Excellent financial health' :
               financialHealthScore >= 60 ? 'Good financial health, with room for improvement' :
               'Consider adjusting your budget to improve financial health'}
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="glass-card p-6 border border-emerald-500/20"
        >
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-5 h-5 text-emerald-500" />
            <h3 className="font-semibold">Income Comparison</h3>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-slate-400 mb-1">Income Percentile</p>
              <p className="text-2xl font-bold text-emerald-500">
                {Math.round(incomePercentile)}th
              </p>
            </div>
            <p className="text-sm text-slate-400">
              Your income is higher than {Math.round(incomePercentile)}% of {filingStatus} filers
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="glass-card p-6 border border-emerald-500/20"
        >
          <div className="flex items-center gap-3 mb-4">
            <PiggyBank className="w-5 h-5 text-emerald-500" />
            <h3 className="font-semibold">Savings Analysis</h3>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-slate-400 mb-1">Monthly Savings Rate</p>
              <p className="text-2xl font-bold text-emerald-500">
                {expenseRatio.savings.toFixed(1)}%
              </p>
            </div>
            <p className="text-sm text-slate-400">
              {expenseRatio.savings >= 20 ? 'Excellent savings rate' :
               expenseRatio.savings >= 15 ? 'Good savings rate' :
               'Consider increasing your savings'}
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="glass-card p-6 border border-emerald-500/20"
        >
          <div className="flex items-center gap-3 mb-4">
            <MapPin className="w-5 h-5 text-emerald-500" />
            <h3 className="font-semibold">Cost of Living</h3>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-slate-400 mb-1">State Index</p>
              <p className="text-2xl font-bold text-emerald-500">
                {costOfLivingIndex[state].toFixed(1)}
              </p>
            </div>
            <p className="text-sm text-slate-400">
              {costOfLivingIndex[state] > 100 
                ? `${state} is ${(costOfLivingIndex[state] - 100).toFixed(1)}% more expensive than average`
                : `${state} is ${(100 - costOfLivingIndex[state]).toFixed(1)}% less expensive than average`}
            </p>
          </div>
        </motion.div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="glass-card p-6 border border-emerald-500/20"
        >
          <h3 className="text-xl font-semibold mb-6">Expense Distribution vs National Average</h3>
          <FinancialChart
            type="bar"
            data={expenseDistributionData}
            height={300}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="glass-card p-6 border border-emerald-500/20"
        >
          <h3 className="text-xl font-semibold mb-6">Income Comparison</h3>
          <FinancialChart
            type="bar"
            data={incomeComparisonData}
            height={300}
          />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className="glass-card p-6 border border-emerald-500/20"
      >
        <h3 className="text-xl font-semibold mb-6">Recommended Adjustments</h3>
        <div className="space-y-6">
          {recommendedAdjustments.map((adjustment, index) => (
            <div key={index} className="glass-card p-4 border border-emerald-500/20">
              <div className="flex justify-between items-baseline mb-2">
                <h4 className="font-medium">{adjustment.category}</h4>
                <span className="text-sm text-red-500">
                  ${Math.abs(adjustment.difference).toLocaleString()} {adjustment.difference < 0 ? 'decrease' : 'increase'} recommended
                </span>
              </div>
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500"
                  style={{ width: `${(adjustment.current / adjustment.recommended) * 100}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-sm text-slate-400">
                <span>Current: ${adjustment.current.toLocaleString()}</span>
                <span>Recommended: ${adjustment.recommended.toLocaleString()}</span>
              </div>
            </div>
          ))}

          {recommendedAdjustments.length === 0 && (
            <div className="text-center py-4 text-emerald-500">
              Your budget allocations are well-balanced! Keep maintaining these healthy financial habits.
            </div>
          )}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.7 }}
        className="glass-card p-6 border border-emerald-500/20"
      >
        <div className="flex items-center gap-3 mb-6">
          <Briefcase className="w-5 h-5 text-emerald-500" />
          <h3 className="text-xl font-semibold">Financial Recommendations</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="font-medium text-emerald-500">Short-term Actions</h4>
            <ul className="space-y-2">
              {expenseRatio.needs > 50 && (
                <li className="flex items-start gap-2 text-slate-300">
                  <DollarSign className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                  Reduce monthly needs expenses by ${(expenses.needs - (income * 0.5)).toFixed(0)}
                </li>
              )}
              {expenseRatio.wants > 30 && (
                <li className="flex items-start gap-2 text-slate-300">
                  <DollarSign className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                  Decrease discretionary spending by ${(expenses.wants - (income * 0.3)).toFixed(0)}
                </li>
              )}
              {expenseRatio.savings < 20 && (
                <li className="flex items-start gap-2 text-slate-300">
                  <DollarSign className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                  Increase monthly savings by ${((income * 0.2) - expenses.savings).toFixed(0)}
                </li>
              )}
              <li className="flex items-start gap-2 text-slate-300">
                <DollarSign className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                Maintain emergency fund of ${(expenses.needs * 6).toLocaleString()}
              </li>
            </ul>
          </div>

          <div className="space-y-4">
            <h4 className="font-medium text-emerald-500">Long-term Goals</h4>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-slate-300">
                <DollarSign className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                Target retirement savings of ${(income * 12 * 0.15).toLocaleString()} annually
              </li>
              <li className="flex items-start gap-2 text-slate-300">
                <DollarSign className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                Keep housing costs under ${(income * 0.28).toLocaleString()} monthly
              </li>
              <li className="flex items-start gap-2 text-slate-300">
                <DollarSign className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                Build investment portfolio of ${(income * 12 * 3).toLocaleString()}
              </li>
              <li className="flex items-start gap-2 text-slate-300">
                <DollarSign className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                Maintain debt-to-income ratio below {(financialRatios.debtToIncome.good * 100).toFixed(0)}%
              </li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
};